<template>
  <div class="quill-editor">
    <slot name="toolbar"></slot>
    <div ref="editor" class="canvas"></div>
  </div>
</template>

<script>
// require sources
import _Quill from 'quill';
import ImageUploader from 'quill-image-uploader';
import QuillResizeImage from 'quill-resize-image';
import 'quill-image-uploader/dist/quill.imageUploader.min.css';

const Quill = window.Quill || _Quill;

const BlockEmbed = Quill.import('blots/block/embed');
const Delta = Quill.import('delta');

// Ensure our custom 'details' toolbar icon exists BEFORE any Quill instances are created
try {
  const icons = Quill.import('ui/icons');
  if (icons && !icons.details) {
    // Simple chevron + lines icon, matching Snow icon sizing
    icons.details = '<svg viewBox="0 0 18 18">\n' +
      '  <polyline points="5 7 9 11 13 7" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></polyline>\n' +
      '  <line x1="3" y1="14" x2="15" y2="14" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>\n' +
      '  <line x1="3" y1="4" x2="15" y2="4" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>\n' +
      '</svg>';
  }
} catch (e) {
  // ignore
}

class FigureBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.innerHTML = value;
    node.setAttribute('contenteditable', 'false');
    node.addEventListener('click', this.handleClick);
    this.syncImageAlignment(node);

    return node;
  }

  static value(node) {
    return node.innerHTML;
  }

  static handleClick(event) {
    if (event.target.tagName === 'FIGCAPTION') {
      event.stopPropagation();
      const figcaption = event.target.parentNode.querySelector('figcaption');

      if (figcaption) {
        const captionText = figcaption.textContent;
        const inputField = document.createElement('input');
        inputField.type = 'text';
        inputField.value = captionText;
        inputField.style.width = '100%';
        inputField.style.boxSizing = 'border-box';

        figcaption.textContent = '';
        figcaption.appendChild(inputField);
        inputField.focus();

        inputField.addEventListener('blur', () => {
          figcaption.textContent = inputField.value;
        });

        inputField.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            figcaption.textContent = inputField.value;
            inputField.blur();
          } else if (e.key === 'Backspace') {
            e.stopPropagation();
          }
        });
      }
    }
  }

  /* The QuillResizeImage plugin is desgiend for <img>, it's adjusted here to apply to <figure> instead.
    It checks what style <img> and apply the same style to <figure> using class */
  static syncImageAlignment(figures) {
    // Check if `figures` is a NodeList or a single element
    const figureList = NodeList.prototype.isPrototypeOf(figures) ? figures : [figures];

    figureList.forEach((figure) => {
      const img = figure.querySelector('img');
      const imgHTML = img.outerHTML;
      let style = img.getAttribute('style');

      const figureClass = figure.classList.toString();

      if (
        imgHTML.includes('display: block; margin: auto;') &&
        !imgHTML.includes('float') &&
        figureClass.includes('align-center')
      )
        return;

      if (imgHTML.includes('display: block; margin: auto;') && !figureClass.includes('align-center')) {
        figure.classList.add('align-center');
        figure.classList.remove('align-left');
        figure.classList.remove('align-right');
        style = style.replace('float: right;', '').replace('float: left;', '');
        img.setAttribute('style', style);
      }

      if (imgHTML.includes('float: left') && !figureClass.includes('align-left')) {
        figure.classList.add('align-left');
        figure.classList.remove('align-right');
        figure.classList.remove('align-center');
        img.setAttribute('style', style.replace('display: block; margin: auto;', ''));
      }

      if (imgHTML.includes('float: right') && !figureClass.includes('align-right')) {
        figure.classList.add('align-right');
        figure.classList.remove('align-left');
        figure.classList.remove('align-center');
        img.setAttribute('style', style.replace('display: block; margin: auto;', ''));
      }
    });
  }
}

class DetailsBlot extends BlockEmbed {
  /**
   * NOTE ABOUT SUMMARY EDITING
   * - The embed root (<details>) remains contenteditable=false so Quill treats it as one atomic block.
   * - The <summary> itself is set to contenteditable=true so users can type directly.
   * - We stop event propagation to prevent Quill selection hijacking, but we DO NOT preventDefault()
   *   on summary clicks, otherwise the caret cannot be placed.
   * - On input/blur we call parentQuill.update('user') so Quill records the embed value changes.
   */

  static ensureStructure(details) {
    const result = { summary: null, body: null };
    if (!details) return result;

    // Ensure a <summary> exists and is the first element
    let summary = details.querySelector('summary');
    if (!summary) {
      summary = document.createElement('summary');
      summary.textContent = 'Section title';
      details.insertBefore(summary, details.firstChild);
    } else if (summary !== details.firstElementChild) {
      details.insertBefore(summary, details.firstChild);
    }

    // Make summary directly editable
    summary.setAttribute('contenteditable', 'true');
    summary.setAttribute('spellcheck', 'true');
    summary.style.outline = 'none';

    // Prevent multiline summary; keep Quill from swallowing keys
    if (!summary.__details_summary_keydown_bound) {
      summary.__details_summary_keydown_bound = true;
      summary.addEventListener('keydown', (e) => {
        // Stop Quill (and other handlers) from hijacking keys
        e.stopPropagation();
        // Some libs attach capture listeners; this helps in those cases
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        // Finish editing on Enter
        if (e.key === 'Enter') {
          e.preventDefault();
          summary.blur();
          return;
        }

        // Space is tricky on <summary> because it can toggle <details>.
        // We prevent the native toggle and manually insert a space at the caret.
        if (e.key === ' ') {
          e.preventDefault();
          details.setAttribute('open', '');

          // Prefer execCommand when available
          try {
            if (document.queryCommandSupported && document.queryCommandSupported('insertText')) {
              document.execCommand('insertText', false, ' ');
              return;
            }
          } catch (_) {}

          // Fallback: insert a text node at the current selection
          try {
            const sel = window.getSelection && window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            range.deleteContents();
            const textNode = document.createTextNode(' ');
            range.insertNode(textNode);
            // Move caret after inserted space
            range.setStartAfter(textNode);
            range.setEndAfter(textNode);
            sel.removeAllRanges();
            sel.addRange(range);
          } catch (_) {}
        }
      }, true);
    }

    // Ensure a single .details-body wraps all non-summary nodes
    let body = details.querySelector('.details-body');
    if (!body) {
      body = document.createElement('div');
      body.className = 'details-body';
      const toMove = [];
      details.childNodes.forEach((n) => {
        if (n !== summary) toMove.push(n);
      });
      toMove.forEach((n) => body.appendChild(n));
      details.appendChild(body);
    }

    result.summary = summary;
    result.body = body;
    return result;
  }

  static attachSummarySync(details) {
    if (!details) return;
    const summary = details.querySelector('summary');
    if (!summary) return;

    if (summary.__details_summary_sync_bound) return;
    summary.__details_summary_sync_bound = true;

    const updateParent = () => {
      try {
        const editorRoot = details.closest('.ql-editor');
        const parentQuill = editorRoot && editorRoot.__quill ? editorRoot.__quill : null;
        if (parentQuill) parentQuill.update('user');
      } catch (e) {}
    };

    const keepOpen = () => details.setAttribute('open', '');

    const stopToggleButKeepTyping = (e) => {
      // Only act when editing summary
      keepOpen();

      // Stop Quill + native summary toggle listeners
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      // Block native toggle on Space/Enter.
      // IMPORTANT: do this on BOTH keydown and keyup.
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();

        // For Space, manually insert a space (because we prevented default)
        if (e.key === ' ') {
          try {
            if (document.queryCommandSupported?.('insertText')) {
              document.execCommand('insertText', false, ' ');
              return;
            }
          } catch (_) {}

          // Fallback insert
          const sel = window.getSelection?.();
          if (!sel || sel.rangeCount === 0) return;
          const range = sel.getRangeAt(0);
          range.deleteContents();
          const textNode = document.createTextNode(' ');
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    };

// Capture-phase listeners so we win the race
    summary.addEventListener('keydown', stopToggleButKeepTyping, true);
    summary.addEventListener('keyup', stopToggleButKeepTyping, true);

// Some browsers still toggle on keypress
    summary.addEventListener('keypress', stopToggleButKeepTyping, true);

    summary.addEventListener('input', () => {
      // keep open while editing
      details.setAttribute('open', '');
      updateParent();
    });

    summary.addEventListener('blur', () => {
      // Ensure not empty
      if (!summary.textContent || !summary.textContent.trim()) {
        summary.textContent = 'Section title';
      }
      details.setAttribute('open', '');
      updateParent();
    });

    // Prevent Quill from stealing pointer events while still allowing caret placement
    summary.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      details.setAttribute('open', '');
    }, true);
    summary.addEventListener('mousedown', (e) => {
      e.stopPropagation();
      details.setAttribute('open', '');
    }, true);
    summary.addEventListener('click', (e) => {
      e.stopPropagation();
      details.setAttribute('open', '');
    }, true);
  }

  static openBodyEditor(details, body) {
    // Inline in-place editing with a nested Quill inside the details body
    if (!details || !body) return;
    if (details.dataset && details.dataset.editing === '1') return;
    if (details.dataset) details.dataset.editing = '1';

    // Ensure it stays open and normalized
    details.setAttribute('open', '');
    try { DetailsBlot.ensureStructure(details); } catch (e) {}

    const previousHTML = body.innerHTML;

    // Suspend parent Quill while editing and remember position
    let parentQuill = null;
    let blotIndex = null;
    try {
      const editorRoot = details.closest('.ql-editor');
      parentQuill = editorRoot && editorRoot.__quill ? editorRoot.__quill : null;
      const blot = Quill.find(details);
      if (parentQuill && blot) {
        blotIndex = parentQuill.getIndex(blot);
        parentQuill.enable(false);
      }
    } catch (e) {}

    // Build inline editor UI
    const container = document.createElement('div');
    // Mark as inline editor container so outer handlers can ignore clicks inside it
    container.className = 'details-inline-editor';
    container.style.border = '1px solid #cbd5e1';
    container.style.borderRadius = '4px';
    container.style.padding = '8px';
    container.style.background = '#fff';
    container.style.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.02)';

    const actions = document.createElement('div');
    actions.style.display = 'flex';
    actions.style.justifyContent = 'flex-end';
    actions.style.gap = '8px';
    actions.style.marginTop = '8px';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.padding = '6px 10px';
    cancelBtn.style.border = '1px solid #cbd5e1';
    cancelBtn.style.borderRadius = '4px';
    cancelBtn.style.background = '#fff';
    cancelBtn.style.cursor = 'pointer';

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.textContent = 'Save';
    saveBtn.style.padding = '6px 12px';
    saveBtn.style.border = '1px solid #0284c7';
    saveBtn.style.borderRadius = '4px';
    saveBtn.style.background = '#0ea5e9';
    saveBtn.style.color = '#fff';
    saveBtn.style.cursor = 'pointer';

    const editorEl = document.createElement('div');
    editorEl.className = 'ql-container ql-snow';
    editorEl.style.minHeight = '120px';

    // Clear body and insert our editor
    body.innerHTML = '';
    body.appendChild(container);
    container.appendChild(editorEl);
    actions.appendChild(cancelBtn);
    actions.appendChild(saveBtn);
    container.appendChild(actions);

    // Create nested Quill
    let nestedQuill = null;
    try {
      const toolbarConfig = [
        ['bold', 'italic', 'underline', 'strike'],
        [{ header: [1, 2, 3, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        [{ align: [] }],
        ['clean'],
      ];
      nestedQuill = new Quill(editorEl, { modules: { toolbar: toolbarConfig }, theme: 'snow' });
      nestedQuill.clipboard.dangerouslyPasteHTML(previousHTML || '<p>Section content...</p>');
      (window.requestAnimationFrame || window.setTimeout)(() => {
        try { nestedQuill.focus(); } catch (e) {}
      }, 0);
    } catch (e) {}

    const cleanup = (restore = false) => {
      try {
        if (!restore && nestedQuill) {
          const html = editorEl.querySelector('.ql-editor')?.innerHTML || '';
          body.innerHTML = html && html !== '<p><br></p>' ? html : '<p>Section content...</p>';
        }
        if (restore) {
          body.innerHTML = previousHTML;
        }
      } catch (e) {}

      // Re-enable parent quill and restore caret after the details blot
      try {
        if (parentQuill) {
          parentQuill.enable(true);
          if (blotIndex != null) parentQuill.setSelection(blotIndex + 1, 0, 'silent');
          parentQuill.update('user');
        }
      } catch (e) {}

      // Flag off
      if (details.dataset) delete details.dataset.editing;
    };

    // Actions
    const cancel = () => cleanup(true);
    const save = () => cleanup(false);
    cancelBtn.addEventListener('click', cancel);
    saveBtn.addEventListener('click', save);

    // Keyboard shortcuts inside inline editor
    container.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        cancel();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        save();
      }
    });
  }

  static create(value) {
    const node = super.create();
    const defaultInner = '<summary>Section title</summary><div class="details-body"><p>Section content...</p></div>';
    // ensure it's opened by default
    node.setAttribute('open', '');
    node.innerHTML = value && typeof value === 'string' && value.trim() ? value : defaultInner;
    node.setAttribute('contenteditable', 'false');

    // Normalize structure to always have <summary> and a single .details-body
    try { DetailsBlot.ensureStructure(node); } catch (e) {}

    // Enable summary editing + syncing to Quill
    try { DetailsBlot.attachSummarySync(node); } catch (e) {}

    // Prevent Quill from hijacking mouse events; allow our editor to receive focus
    const captureHandler = (event) => {
      const target = event.target;
      const { summary, body } = DetailsBlot.ensureStructure(node);
      const insideInline = target && target.closest && (target.closest('.details-inline-editor') || target.closest('.ql-editor'));

      // If inline editor is active and the click is inside it, allow normal mouse behavior
      if (node.dataset && node.dataset.editing === '1' && insideInline) {
        return;
      }

      // SUMMARY: allow caret placement (do NOT preventDefault)
      if (target && (target.tagName === 'SUMMARY' || target.closest('summary'))) {
        node.setAttribute('open', '');
        event.stopPropagation();
        return;
      }

      // BODY: open nested editor on clicks inside details
      if (node.contains(target)) {
        event.preventDefault();
        event.stopPropagation();
        if (!(node.dataset && node.dataset.editing === '1')) {
          try {
            DetailsBlot.openBodyEditor(node, body);
          } catch (e) {}
        }
      }
    };

    // Use pointerdown where available to win the race with native <details> toggle and Quill selection
    node.addEventListener('pointerdown', captureHandler, true);
    node.addEventListener('mousedown', captureHandler, true);

    // Keep these handlers, but handleClick no longer swaps summary for an <input>
    node.addEventListener('click', (event) => this.handleClick(event));
    node.addEventListener('dblclick', (event) => this.handleClick(event));

    return node;
  }

  static value(node) {
    return node.innerHTML;
  }

  static handleClick(event) {
    const details = event.currentTarget; // the blot root is the <details> element
    if (!details) return;
    const { target } = event;

    // If inline editor is active, allow clicks inside it to behave normally
    if (details.dataset && details.dataset.editing === '1') {
      const insideInline = target && target.closest && (target.closest('.details-inline-editor') || target.closest('.ql-editor'));
      if (insideInline) return;
    }

    // Summary is contenteditable; no input replacement needed
    if (target && (target.tagName === 'SUMMARY' || target.closest('summary'))) {
      event.stopPropagation();
      details.setAttribute('open', '');
      try { DetailsBlot.ensureStructure(details); } catch (e) {}
      try { DetailsBlot.attachSummarySync(details); } catch (e) {}
      return;
    }

    // Edit body HTML on click inside details-body
    const body = details.querySelector('.details-body');
    if (body && (target === body || body.contains(target))) {
      event.preventDefault();
      event.stopPropagation();
      DetailsBlot.openBodyEditor(details, body);
    }
  }
}

FigureBlot.blotName = 'figure';
FigureBlot.tagName = 'figure';

DetailsBlot.blotName = 'details';
DetailsBlot.tagName = 'details';

Quill.register(FigureBlot);
Quill.register(DetailsBlot);
Quill.register('modules/imageUploader', ImageUploader);
Quill.register('modules/resize', QuillResizeImage);

// export
export default {
  name: 'QuillEditorLocal',
  props: {
    content: String,
    value: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    globalOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    createImage: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      _options: {},
      _content: '',
      isImageUploaded: false,
      defaultOptions: {
        theme: 'snow',
        boundary: document.body,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote'],
            [{ header: 3 }, { header: 4 }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ header: [1, 2, 3, 4, false] }],
            [{ align: [] }],
            ['clean'],
            ['details'],
            ['link', 'image'],
            [{ container: [['image']] }],
          ],
          resize: {
            locale: {},
          },
          imageUploader: {
            upload: async (file) => {
              const imageUrl = await this.createImage(file, 'Inline');

              const canLoadImage = () =>
                new Promise((resolve) => {
                  const probe = new Image();
                  probe.onload = () => resolve(true);
                  probe.onerror = () => resolve(false);
                  probe.src = imageUrl;
                });

              const maxWaitTime = 20000;
              const maxCheckInterval = 4000;
              const startTime = Date.now();
              let attempt = 0;

              while (Date.now() - startTime < maxWaitTime) {
                // eslint-disable-next-line no-await-in-loop
                if (await canLoadImage()) break;

                attempt += 1;
                const checkInterval = Math.min(1000 * 2 ** (attempt - 1), maxCheckInterval);
                // Don't sleep out the rest of the window when there is no time left to probe again
                if (Date.now() + checkInterval - startTime >= maxWaitTime) break;

                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => {
                  setTimeout(resolve, checkInterval);
                });
              }

              this.isImageUploaded = true;

              return imageUrl;
            },
          },
        },
      },
    };
  },
  watch: {
    // Watch content change
    content(newVal, _oldVal) {
      if (this.quill) {
        if (newVal && newVal !== this._content) {
          this._content = newVal;
          this.quill.html = newVal;
        } else if (!newVal) {
          this.quill.setText('');
        }
      }
    },
    // Watch content change
    value(newVal, oldVal) {
      if (this.quill) {
        if (newVal && newVal !== this._content) {
          this._content = newVal;
          this.quill.html = newVal;
        } else if (!newVal) {
          this.quill.setText('');
        }
      }
    },
    // Watch disabled change
    disabled(newVal, _oldVal) {
      if (this.quill) {
        this.quill.enable(!newVal);
      }
    },
  },
  mounted() {
    this.initialize();
  },
  beforeDestroy() {
    this.quill = null;
    delete this.quill;
  },
  methods: {
    // Clean HTML by removing editor-specific attributes
    cleanHTML(html) {
      if (!html) return html;

      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Get all elements and remove editor-specific attributes
      const allElements = tempDiv.querySelectorAll('*');
      allElements.forEach((el) => {
        el.removeAttribute('open');
        el.removeAttribute('contenteditable');
        el.removeAttribute('spellcheck');
        el.removeAttribute('style');
      });

      return tempDiv.innerHTML;
    },
    // Programmatically insert a Details section at the current cursor position
    insertDetailsAtCursor() {
      if (!this.quill) return;
      try {
        const range = this.quill.getSelection(true);
        const index = range ? range.index : this.quill.getLength();
        // Use the existing embed + handler behavior
        this.quill.insertEmbed(index, 'details', '', 'user');
        this.quill.insertText(index + 1, '\n', 'user');
        this.quill.setSelection(index + 2, 0, 'silent');
      } catch (e) {
        // no-op
      }
    },
    // Init Quill instance
    initialize() {
      if (this.$el) {
        // Options
        this._options = { ...this.defaultOptions, ...this.globalOptions, ...this.options };

        // Instance
        this.quill = new Quill(this.$refs.editor, this._options);

        this.quill.enable(false);

        // Set editor content
        if (this.value || this.content) {
          this.quill.clipboard.dangerouslyPasteHTML(this.value);
          this.quill.html = this.value || this.content;
        }

        // Disabled editor
        if (!this.disabled) {
          this.quill.enable(true);
        }

        // Mark model as touched if editor lost focus
        this.quill.on('selection-change', (range) => {
          if (!range) {
            this.$emit('blur', this.quill);
          } else {
            this.$emit('focus', this.quill);
          }
        });

        // Update model if text changes
        this.quill.on('text-change', (delta, oldDelta, source) => {
          let html = this.$refs.editor.children[0].innerHTML;
          const { quill } = this;
          const text = this.quill.getText();
          if (html === '<p><br></p>') html = '';
          this._content = html;
          this.$emit('input', this._content);

          // Clean HTML before emitting
          const cleanedHtml = this.cleanHTML(html);
          this.$emit('change', { html: cleanedHtml, text, quill });
        });

        // Add toolbar handler for custom 'details' button
        const toolbar = this.quill.getModule('toolbar');
        if (toolbar) {
          // Ensure icon is defined (redundant with top-level guard, but safe)
          try {
            const icons = Quill.import('ui/icons');
            if (icons && !icons.details) {
              icons.details = '<svg viewBox="0 0 18 18">\n' +
                '  <polyline points="5 7 9 11 13 7" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></polyline>\n' +
                '  <line x1="3" y1="14" x2="15" y2="14" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>\n' +
                '  <line x1="3" y1="4" x2="15" y2="4" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>\n' +
                '</svg>';
            }
          } catch (e) {}

          toolbar.addHandler('details', () => {
            const range = this.quill.getSelection(true);
            const index = range ? range.index : this.quill.getLength();
            // Insert the details embed blot at current cursor
            this.quill.insertEmbed(index, 'details', '', 'user');
            // Move cursor after the embed and add a newline for easier typing after
            this.quill.insertText(index + 1, '\n', 'user');
            this.quill.setSelection(index + 2, 0, 'silent');
          });

          // Post-init fallback: if the button is empty, inject our SVG and a title
          try {
            const toolbarElem = this.$el.querySelector('.ql-toolbar');
            const btn = toolbarElem && toolbarElem.querySelector('button.ql-details');
            if (btn && (!btn.innerHTML || btn.innerHTML.trim() === '')) {
              btn.innerHTML = '<svg viewBox="0 0 18 18">\n' +
                '  <polyline points="5 7 9 11 13 7" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></polyline>\n' +
                '  <line x1="3" y1="14" x2="15" y2="14" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>\n' +
                '  <line x1="3" y1="4" x2="15" y2="4" stroke="currentColor" stroke-width="1" stroke-linecap="round"></line>\n' +
                '</svg>';
              btn.setAttribute('title', 'Details');
              btn.setAttribute('aria-label', 'Insert details');
            }
          } catch (e) {}
        }

        // Delegated handlers for existing <details> not created by our blot
        // NOTE: summary is contenteditable; we stop propagation but do not preventDefault
        const root = this.quill.root;
        const delegatedPointer = (e) => {
          const target = e.target;
          if (!target) return;
          const details = target.closest && target.closest('details');
          if (!details || !root.contains(details)) return;

          const summary = details.querySelector('summary');
          const clickedSummary = summary && (target === summary || summary.contains(target));
          const insideInline = target.closest && (target.closest('.details-inline-editor') || target.closest('.ql-editor'));

          // If an inline details editor is active and the click is inside it, let it handle the event
          if (details.dataset && details.dataset.editing === '1' && insideInline) {
            return;
          }

          try { DetailsBlot.ensureStructure(details); } catch (err) {}
          try { DetailsBlot.attachSummarySync(details); } catch (err) {}

          if (clickedSummary) {
            // Allow caret placement: do NOT preventDefault
            e.stopPropagation();
            details.setAttribute('open', '');
            return;
          }

          // Any non-summary click: open nested body editor
          e.preventDefault();
          e.stopPropagation();
          const body = details.querySelector('.details-body');
          if (body) {
            try { DetailsBlot.openBodyEditor(details, body); } catch (err) {}
          }
        };

        const delegatedKeydown = (e) => {
          const target = e.target;
          if (!target) return;
          const details = target.closest && target.closest('details');
          if (!details || !root.contains(details)) return;

          const summary = details.querySelector('summary');
          const onSummary = summary && (target === summary || summary.contains(target));
          if (!onSummary) return;

          // Let summary handle Enter (we blur on Enter in ensureStructure)
          e.stopPropagation();
        };

        root.addEventListener('pointerdown', delegatedPointer, true);
        root.addEventListener('click', delegatedPointer, true);
        root.addEventListener('keydown', delegatedKeydown, true);
        root.addEventListener('dblclick', delegatedPointer, true);

        this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
          const allowedAttributes = [
            'bold',
            'italic',
            'underline',
            'strike',
            'blockquote',
            'header',
            'list',
            'script',
            'indent',
            'direction',
            'align',
            'link',
            'image',
          ];

          delta.forEach((e) => {
            if (e && e.attributes) {
              Object.keys(e.attributes).forEach((attr) => {
                if (!allowedAttributes.includes(attr)) {
                  delete e.attributes[attr];
                }
              });
            }
          });

          return delta;
        });

        this.quill.on('text-change', async (delta) => {
          const hasImageOp = delta.ops.some((op) => op.insert && op.insert.image);

          const htmlObject = document.createElement('div');
          htmlObject.innerHTML = this.quill.root.innerHTML;

          const images = htmlObject.querySelectorAll('img');

          const figures = htmlObject.querySelectorAll('figure');

          const oldFigures = [];
          figures.forEach((figure) => {
            oldFigures.push(figure.outerHTML);
          });

          FigureBlot.syncImageAlignment(figures);
          let hasFigureChanged = false;
          figures.forEach((figure, index) => {
            if (figure.outerHTML !== oldFigures[index]) {
              hasFigureChanged = true;
            }
          });

          this.quill.html = htmlObject.innerHTML;
          if (hasFigureChanged) await this.quill.clipboard.dangerouslyPasteHTML(htmlObject.innerHTML);

          if (!hasImageOp || !this.isImageUploaded) return;

          images.forEach(async (img) => {
            if (img.parentNode.nodeName.toLowerCase() !== 'figure') {
              const figure = document.createElement('figure');
              figure.classList.add('image');

              img.parentNode.insertBefore(figure, img);
              figure.appendChild(img);

              if (!img.parentElement.querySelector('figcaption')) {
                const figcaption = document.createElement('figcaption');
                figcaption.textContent = 'Click on the image to edit this caption';
                figure.appendChild(figcaption);
              }

              const lineBreak = document.createElement('br');
              figure.parentNode.insertBefore(lineBreak, figure.nextSibling);
            }
          });

          this.isImageUploaded = false;
          await this.quill.clipboard.dangerouslyPasteHTML(htmlObject.innerHTML);
          this.quill.html = htmlObject.innerHTML;
        });

        // Emit ready event
        this.$emit('ready', this.quill);
      }
    },
  },
};
</script>

<style lang="scss">
.canvas {
  min-height: 500px;
}

p {
  margin-bottom: 16px !important;
  font-family: 'Noto Serif' !important;
  font-size: 1.2rem !important;
  margin: 1em 0 1em 0 !important;
}

h3 {
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  line-height: 1.75rem !important;
  letter-spacing: normal !important;
  padding: 1rem 0 0.3rem 0 !important;
}

h4 {
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  line-height: 1.75rem !important;
  letter-spacing: normal !important;
  padding: 1rem 0 0.3rem 0 !important;
}

figure {
  display: table;

  &.align-left {
    float: left;
    margin-right: 10px;
  }

  &.align-right {
    float: right;
    margin-left: 10px;
  }

  &.align-center {
    margin: auto;
  }

  & > img {
    display: block;
    max-width: 100%;
  }

  & > figcaption {
    display: table-caption;
    caption-side: bottom;
    padding: 12px;
    background-color: #ebdcff;
    color: #260059;
    text-align: center;
    font-family: 'Noto Sans', sans-serif;
    font-size: 1em !important;
    margin-bottom: 0.5rem;
  }
}

blockquote {
  @media (max-width: 600px) {
    margin: 1.5rem;
  }
  font-weight: bold;
  margin: 3rem !important;
  font-size: 1rem;
  text-align: center;
  border: none !important;
  font-family: 'Noto Serif';
}

/* Details section styles */
details {
  border-left: 3px solid #ddd;
  padding: 0.5rem 0 0.5rem 0.75rem;
  margin: 1rem 0;
}
details[open] {
  border-color: #bbb;
}
details > summary {
  list-style: none;
  cursor: text; /* changed from pointer -> text so it feels editable */
  font-weight: 600;
  font-family: 'Noto Sans', sans-serif;
  margin-bottom: 0.5rem;
}
details > summary::-webkit-details-marker {
  display: none;
}
.details-body {
  font-family: 'Noto Serif';
}

/* Optional: give editable summary a subtle focus ring */
details > summary:focus {
  outline: 2px solid rgba(14, 165, 233, 0.35);
  outline-offset: 2px;
}
</style>
